import React, { useEffect, useMemo, useState } from "react";

import TitleButtons from "../../../../../components/TitleButtons";
import TextField from "../../../../../components/TextField";
import TextFieldRow from "../../../../../components/TextFieldRow";
import Button from "../../../../../components/Button";

import { useAuth } from "../../../../../context/auth-context";

/** =========================================================
 * Endpoints
 * ======================================================= */

const API_URL =
  "https://api.eva-googlemarine.com/departamentos/coordenacao/empresa-clientes";

const API_COLABS =
  "https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores-coordenacao";

/** =========================================================
 * Tipos
 * ======================================================= */

type Notice = {
  type: "success" | "error" | "notify";
  text: string;
};

type ColabApiItem = {
  id: string | number;
  nome?: string | null;
  nome_completo?: string | null;
  departamento?: string | null;
};

type ColabOpt = {
  id: number;
  label: string;
};

type ApiResponse<T = unknown> = {
  ok?: boolean;
  erro?: string;
  message?: string;
  erros?: string[];
  data?: T;
  trace_id?: string;
};

/** =========================================================
 * Autenticação
 * ======================================================= */

function getStoredAccessToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const tokenKeys = ["accessToken", "eva:accessToken", "access_token"];

  try {
    for (const key of tokenKeys) {
      const value = window.localStorage.getItem(key);

      if (value && value.trim()) {
        return value.trim();
      }
    }
  } catch {
    return null;
  }

  return null;
}

function resolveEmpresaId(sessao: any): number | null {
  const raw =
    sessao?.empresa_ativa_id ??
    sessao?.empresa_id ??
    sessao?.empresa?.id ??
    sessao?.empresa_principal_id ??
    null;

  const parsed = Number(raw);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function buildAuthHeaders(contentType = false): Record<string, string> {
  const token = getStoredAccessToken();

  return {
    Accept: "application/json",

    ...(contentType
      ? {
          "Content-Type": "application/json; charset=utf-8",
        }
      : {}),

    ...(token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {}),
  };
}

async function readJsonSafe<T = any>(
  response: Response
): Promise<ApiResponse<T>> {
  const text = await response.text().catch(() => "");

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    return {
      ok: false,
      erro: text,
    };
  }
}

/** =========================================================
 * Utilitários
 * ======================================================= */

const onlyDigits = (value: unknown): string =>
  String(value ?? "").replace(/\D+/g, "");

function maskCNPJ(value: string): string {
  const digits = onlyDigits(value).slice(0, 14);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 5) {
    return digits.replace(/^(\d{2})(\d+)/, "$1.$2");
  }

  if (digits.length <= 8) {
    return digits.replace(/^(\d{2})(\d{3})(\d+)/, "$1.$2.$3");
  }

  if (digits.length <= 12) {
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, "$1.$2.$3/$4");
  }

  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2}).*/,
    "$1.$2.$3/$4-$5"
  );
}

function asInt(value: unknown): number | null {
  const parsed = Number(String(value ?? "").trim());

  if (!Number.isFinite(parsed)) {
    return null;
  }

  const integer = Math.trunc(parsed);

  return integer > 0 ? integer : null;
}

/** =========================================================
 * Aviso
 * ======================================================= */

const NoticeBanner: React.FC<{
  notice: Notice;
  onClose?: () => void;
  sticky?: boolean;
}> = ({ notice, onClose, sticky = true }) => {
  let background: string;
  let color: string;
  let border: string;

  switch (notice.type) {
    case "success":
      background = "#e6ffed";
      color = "#0f5132";
      border = "#badbcc";
      break;

    case "error":
      background = "#ffecec";
      color = "#842029";
      border = "#f5c2c7";
      break;

    default:
      background = "#e7f1ff";
      color = "#084298";
      border = "#b6d4fe";
      break;
  }

  return (
    <div
      role="alert"
      style={{
        margin: "12px 0",
        padding: "10px 12px",
        borderRadius: 8,
        background,
        color,
        border: `1px solid ${border}`,
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ flex: 1 }}>{notice.text}</span>

        {onClose && (
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

/** =========================================================
 * Página
 * ======================================================= */

const RegistrarEmpresa: React.FC = () => {
  const { sessao, loading: authLoading, refresh: refreshAuth } = useAuth();

  const empresaId = useMemo(
    () => resolveEmpresaId(sessao),
    [
      sessao?.empresa_ativa_id,
      sessao?.empresa_id,
      sessao?.empresa?.id,
      sessao?.empresa_principal_id,
    ]
  );

  const [razao, setRazao] = useState("");

  const [fantasia, setFantasia] = useState("");

  const [cnpjView, setCnpjView] = useState("");

  const [coordenadorLabel, setCoordenadorLabel] = useState("");

  const [coordenadorId, setCoordenadorId] = useState<number | null>(null);

  const [colabs, setColabs] = useState<ColabOpt[]>([]);

  const [loadingColabs, setLoadingColabs] = useState(false);

  const [erroColabs, setErroColabs] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);

  const [notice, setNotice] = useState<Notice | null>(null);

  const cnpjDigits = onlyDigits(cnpjView);

  const onChangeCNPJ = (value: string) => {
    setCnpjView(maskCNPJ(value));
  };

  const colabDropdown = useMemo(
    () =>
      colabs.length
        ? colabs.map((colaborador) => colaborador.label)
        : undefined,
    [colabs]
  );

  /**
   * Resolve o ID quando o usuário seleciona
   * um item válido da lista.
   */
  useEffect(() => {
    const selected = colabs.find(
      (colaborador) => colaborador.label === coordenadorLabel
    );

    setCoordenadorId(selected?.id ?? null);
  }, [coordenadorLabel, colabs]);

  /**
   * Limpa o coordenador ao trocar de empresa ativa.
   */
  useEffect(() => {
    setCoordenadorLabel("");
    setCoordenadorId(null);
    setColabs([]);
  }, [empresaId]);

  /**
   * Carrega coordenadores autenticados.
   */
  useEffect(() => {
    let cancelled = false;

    const controller = new AbortController();

    async function loadColabs() {
      if (authLoading) {
        return;
      }

      setLoadingColabs(true);
      setErroColabs(null);

      try {
        if (!empresaId) {
          throw new Error("Empresa ativa não identificada na sessão.");
        }

        let token = getStoredAccessToken();

        /*
         * Caso o contexto já tenha carregado,
         * mas o token ainda não esteja no storage,
         * tenta atualizar a autenticação.
         */
        if (!token) {
          await refreshAuth().catch(() => undefined);

          token = getStoredAccessToken();
        }

        if (!token) {
          throw new Error(
            "Token de autenticação não encontrado. Faça login novamente."
          );
        }

        const response = await fetch(API_COLABS, {
          method: "GET",

          headers: {
            Accept: "application/json",

            Authorization: `Bearer ${token}`,
          },

          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        const json = await readJsonSafe<ColabApiItem[]>(response);

        if (!response.ok || json?.ok === false || !Array.isArray(json?.data)) {
          const message =
            json?.erro ||
            json?.message ||
            `Falha ao carregar colaboradores. HTTP ${response.status}`;

          throw new Error(message);
        }

        const options: ColabOpt[] = json.data
          .map((colaborador: ColabApiItem) => {
            const id = asInt(colaborador.id);

            const nome = String(
              colaborador.nome ?? colaborador.nome_completo ?? ""
            ).trim();

            if (!id || !nome) {
              return null;
            }

            return {
              id,
              label: `${nome.toUpperCase()} (${id})`,
            };
          })
          .filter((item): item is ColabOpt => item !== null);

        options.sort((first, second) =>
          first.label.localeCompare(second.label, "pt-BR")
        );

        if (!cancelled) {
          setColabs(options);
          setErroColabs(null);
        }
      } catch (error: any) {
        if (error?.name === "AbortError") {
          return;
        }

        if (!cancelled) {
          setColabs([]);

          setErroColabs(error?.message || "Falha ao carregar colaboradores.");
        }
      } finally {
        if (!cancelled) {
          setLoadingColabs(false);
        }
      }
    }

    void loadColabs();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [authLoading, empresaId, refreshAuth]);

  function clearAll() {
    setRazao("");
    setFantasia("");
    setCnpjView("");
    setCoordenadorLabel("");
    setCoordenadorId(null);
  }

  const canSubmit = useMemo(() => {
    const requiredFieldsOk =
      razao.trim().length > 0 &&
      fantasia.trim().length > 0 &&
      cnpjDigits.length === 14;

    const coordinatorOk = coordenadorId !== null;

    return (
      requiredFieldsOk && coordinatorOk && Boolean(empresaId) && !authLoading
    );
  }, [razao, fantasia, cnpjDigits, coordenadorId, empresaId, authLoading]);

  async function handleRegister() {
    setNotice(null);

    const errors: string[] = [];

    if (!empresaId) {
      errors.push("Empresa ativa não identificada.");
    }

    if (!razao.trim()) {
      errors.push("Informe a Razão Social.");
    }

    if (!fantasia.trim()) {
      errors.push("Informe o Nome Fantasia.");
    }

    if (!cnpjDigits || cnpjDigits.length !== 14) {
      errors.push("CNPJ inválido.");
    }

    if (!coordenadorId) {
      errors.push("Selecione um Coordenador Responsável válido.");
    }

    if (errors.length > 0) {
      setNotice({
        type: "error",
        text: errors.join(" "),
      });

      return;
    }

    let token = getStoredAccessToken();

    if (!token) {
      await refreshAuth().catch(() => undefined);

      token = getStoredAccessToken();
    }

    if (!token) {
      setNotice({
        type: "error",
        text: "Token de autenticação não encontrado. Faça login novamente.",
      });

      return;
    }

    const payload = {
      razao_social: razao.trim(),

      nome_fantasia: fantasia.trim(),

      cnpj: cnpjDigits,

      colaborador_interno_responsavel: coordenadorId,
    };

    setSubmitting(true);

    try {
      const response = await fetch(API_URL, {
        method: "POST",

        headers: {
          "Content-Type": "application/json; charset=utf-8",

          Accept: "application/json",

          Authorization: `Bearer ${token}`,
        },

        body: JSON.stringify(payload),

        credentials: "include",
        cache: "no-store",
      });

      const json = await readJsonSafe<any>(response);

      if (!response.ok || json?.ok === false) {
        const message =
          json?.erro ||
          (Array.isArray(json?.erros) ? json.erros.join(" | ") : null) ||
          json?.message ||
          `Falha ao registrar empresa. HTTP ${response.status}`;

        setNotice({
          type: "error",
          text: json?.trace_id
            ? `${message} Código: ${json.trace_id}`
            : message,
        });

        return;
      }

      setNotice({
        type: "success",
        text: `Empresa registrada com sucesso. ID ${
          json?.data?.id ?? ""
        }`.trim(),
      });

      clearAll();
    } catch (error: any) {
      setNotice({
        type: "error",
        text: `Erro de rede: ${error?.message || "tente novamente."}`,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      <TitleButtons title="Departamento Pessoal - Registrar Empresa" />

      {(notice?.type === "success" || notice?.type === "notify") && (
        <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
      )}

      {erroColabs && (
        <NoticeBanner
          notice={{
            type: "notify",
            text: `Aviso: não foi possível carregar os coordenadores. ${erroColabs}`,
          }}
          onClose={() => setErroColabs(null)}
        />
      )}

      <div
        style={{
          display: "grid",
          gap: 10,
          width: "100%",
        }}
      >
        <div
          style={{
            width: "100%",
          }}
        >
          <TextField
            label="Razão Social *"
            value={razao}
            onChange={setRazao}
            placeholder="Ex.: Marinetec Equipamentos e Serviços LTDA"
            uppercase
            style={{
              width: "100%",
            }}
          />
        </div>

        <div
          style={{
            width: "100%",
          }}
        >
          <TextField
            label="Nome Fantasia *"
            value={fantasia}
            onChange={setFantasia}
            placeholder="Ex.: Marinetec"
            uppercase
            style={{
              width: "100%",
            }}
          />
        </div>

        <div
          style={{
            width: "100%",
          }}
        >
          <TextField
            label="CNPJ *"
            value={cnpjView}
            onChange={onChangeCNPJ}
            placeholder="00.000.000/0000-00"
            style={{
              width: "100%",
            }}
          />
        </div>

        <TextFieldRow
          center={{
            label: "Coordenador Responsável *",

            value: coordenadorLabel,

            onChange: setCoordenadorLabel,

            placeholder: authLoading
              ? "CARREGANDO SESSÃO..."
              : loadingColabs
              ? "CARREGANDO COORDENADORES..."
              : erroColabs
              ? "FALHA AO CARREGAR COORDENADORES"
              : colabs.length
              ? "SELECIONE O COORDENADOR"
              : "SEM COORDENADORES DISPONÍVEIS",

            options: colabDropdown,
          }}
        />

        {!loadingColabs && coordenadorLabel.trim() && !coordenadorId && (
          <div
            style={{
              fontSize: 12,
              color: "#842029",
            }}
          >
            Selecione um nome da lista para vincular o ID do colaborador.
          </div>
        )}
      </div>

      {notice?.type === "error" && (
        <NoticeBanner
          notice={notice}
          onClose={() => setNotice(null)}
          sticky={false}
        />
      )}

      <div
        style={{
          marginTop: 12,
        }}
      >
        <Button
          text={submitting ? "Registrando..." : "Registrar Empresa"}
          onClick={handleRegister}
          disabled={submitting || !canSubmit}
        />
      </div>
    </div>
  );
};

export default RegistrarEmpresa;
